import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  WebSocketMessage,
  TicketMessage,
  TicketUpdate,
  SLAAlert,
  TypingIndicator,
  UserStatusUpdate,
  NotificationMessage,
  Message,
} from '../models/support.models';

// ============================================================================
// SERVICE
// ============================================================================

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private connections: Map<string, WebSocket> = new Map();
  private messageHandlers: Map<string, (data: any) => void> = new Map();
  private connectionStatus = new BehaviorSubject<boolean>(false);
  private messageSubject = new Subject<WebSocketMessage>();

  public connectionStatus$ = this.connectionStatus.asObservable();
  public messages$ = this.messageSubject.asObservable();

  constructor() {}

  // ============================================================================
  // CONNECTION MANAGEMENT
  // ============================================================================

  connect(endpoint: string, token?: string): WebSocket {
    const url = this.buildWebSocketUrl(endpoint, token);
    const ws = new WebSocket(url);
    
    ws.onopen = () => {
      console.log(`WebSocket connected to ${endpoint}`);
      this.connectionStatus.next(true);
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleMessage(endpoint, data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };
    
    ws.onerror = (error) => {
      console.error(`WebSocket error for ${endpoint}:`, error);
      this.connectionStatus.next(false);
    };
    
    ws.onclose = () => {
      console.log(`WebSocket disconnected from ${endpoint}`);
      this.connections.delete(endpoint);
      this.messageHandlers.delete(endpoint);
      this.connectionStatus.next(false);
      
      // Attempt to reconnect after a delay
      setTimeout(() => {
        if (!this.connections.has(endpoint)) {
          this.reconnect(endpoint, token);
        }
      }, 5000);
    };
    
    this.connections.set(endpoint, ws);
    return ws;
  }

  private buildWebSocketUrl(endpoint: string, token?: string): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = environment.api || `${protocol}//${window.location.host}`;
    const url = new URL(endpoint, host);
    
    if (token) {
      url.searchParams.set('token', token);
    }
    
    return url.toString();
  }

  private reconnect(endpoint: string, token?: string): void {
    console.log(`Attempting to reconnect to ${endpoint}`);
    this.connect(endpoint, token);
  }

  disconnect(endpoint: string): void {
    const ws = this.connections.get(endpoint);
    if (ws) {
      ws.close();
      this.connections.delete(endpoint);
      this.messageHandlers.delete(endpoint);
    }
  }

  // ============================================================================
  // MESSAGE HANDLING
  // ============================================================================

  private handleMessage(endpoint: string, data: WebSocketMessage): void {
    // Emit to general message stream
    this.messageSubject.next(data);
    
    // Call specific handler if registered
    const handler = this.messageHandlers.get(endpoint);
    if (handler) {
      handler(data);
    }
  }

  subscribe(endpoint: string, handler: (data: WebSocketMessage) => void): void {
    this.messageHandlers.set(endpoint, handler);
  }

  unsubscribe(endpoint: string): void {
    this.messageHandlers.delete(endpoint);
  }

  // ============================================================================
  // MESSAGE SENDING
  // ============================================================================

  send(endpoint: string, data: any): void {
    const ws = this.connections.get(endpoint);
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(data));
    } else {
      console.warn(`WebSocket not connected for endpoint: ${endpoint}`);
    }
  }

  // ============================================================================
  // TICKET-SPECIFIC METHODS
  // ============================================================================

  joinTicketRoom(ticketId: string, token?: string): WebSocket {
    const endpoint = `ws/tickets/${ticketId}`;
    return this.connect(endpoint, token);
  }

  leaveTicketRoom(ticketId: string): void {
    const endpoint = `ws/tickets/${ticketId}`;
    this.disconnect(endpoint);
  }

  sendTicketMessage(ticketId: string, message: any): void {
    const endpoint = `ws/tickets/${ticketId}`;
    this.send(endpoint, {
      type: 'ticket_message',
      data: message,
      timestamp: new Date()
    });
  }

  sendTypingIndicator(ticketId: string, userId: string, userName: string, isTyping: boolean): void {
    const endpoint = `ws/tickets/${ticketId}`;
    this.send(endpoint, {
      type: 'typing_indicator',
      data: {
        ticketId,
        userId,
        userName,
        isTyping
      },
      timestamp: new Date()
    });
  }

  // ============================================================================
  // AGENT-SPECIFIC METHODS
  // ============================================================================

  joinAgentRoom(agentId: string, token?: string): WebSocket {
    const endpoint = `ws/agent/${agentId}`;
    return this.connect(endpoint, token);
  }

  leaveAgentRoom(agentId: string): void {
    const endpoint = `ws/agent/${agentId}`;
    this.disconnect(endpoint);
  }

  updateAgentStatus(agentId: string, status: 'online' | 'away' | 'offline' | 'busy'): void {
    const endpoint = `ws/agent/${agentId}`;
    this.send(endpoint, {
      type: 'agent_status_update',
      data: {
        agentId,
        status,
        lastSeen: new Date()
      },
      timestamp: new Date()
    });
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  isConnected(endpoint: string): boolean {
    const ws = this.connections.get(endpoint);
    return ws ? ws.readyState === WebSocket.OPEN : false;
  }

  getConnectionCount(): number {
    return this.connections.size;
  }

  closeAllConnections(): void {
    this.connections.forEach((ws, endpoint) => {
      this.disconnect(endpoint);
    });
  }

  // ============================================================================
  // LEGACY METHODS FOR BACKWARD COMPATIBILITY
  // ============================================================================

  joinRoom(roomId: string, token?: string): WebSocket {
    return this.joinTicketRoom(roomId, token);
  }

  onMessage(): Observable<WebSocketMessage> {
    return this.messages$;
  }

  sendTyping(ticketId: string, isTyping: boolean): void {
    // This method is used by components, we'll use a default user for now
    this.sendTypingIndicator(ticketId, 'current-user', 'Current User', isTyping);
  }
} 